<template>
  <section class="page" data-testid="page-personas">
    <header class="page-header">
      <div><h1>人设</h1><p>管理人设档案、会话绑定和创建流程。</p></div>
      <button class="button primary" :disabled="loading" @click="load">{{ loading ? "刷新中..." : "刷新" }}</button>
    </header>

    <div class="metrics-grid">
      <MetricCard label="人设总数" :value="items.length" />
      <MetricCard label="有草稿" :value="draftCount" />
      <MetricCard label="学习中" :value="learningCount" />
      <MetricCard label="绑定会话" :value="boundSessionCount" />
      <MetricCard label="当前匹配" :value="filteredItems.length" />
    </div>
    <p v-if="error" class="error-text">{{ error }}</p>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>

    <div class="split">
      <div class="panel">
        <h2>人设工作台</h2>
        <div class="toolbar">
          <input v-model="query" placeholder="搜索名称、模式、绑定会话" />
          <button class="button" :class="{ primary: filter === 'all' }" @click="filter = 'all'">全部</button>
          <button class="button" :class="{ primary: filter === 'draft' }" @click="filter = 'draft'">有草稿</button>
          <button class="button" :class="{ primary: filter === 'learning' }" @click="filter = 'learning'">学习中</button>
          <button class="button" :class="{ primary: filter === 'bound' }" @click="filter = 'bound'">已绑定</button>
        </div>
        <DataTable :columns="columns" :rows="filteredItems" row-key="name">
          <template #name="{ row }">
            <RouterLink class="button small" :to="`/personas/${encodeURIComponent(row.name)}`">{{ row.name }}</RouterLink>
          </template>
          <template #mode="{ row }"><StatusBadge :status="row.mode || 'chat'" /></template>
          <template #published="{ row }">{{ row.published_revision ? `#${row.published_revision.revision_no}` : "-" }}</template>
          <template #draft="{ row }">
            <StatusBadge :status="row.has_unpublished_changes ? 'draft' : 'clean'" />
          </template>
          <template #learning="{ row }">{{ learningLabel(row.learning) }}</template>
          <template #sessions="{ row }">{{ (row.bound_sessions || []).map((item: any) => item.context_key).join(", ") || "-" }}</template>
        </DataTable>
      </div>

      <div>
        <div class="panel">
          <h2>创建人设</h2>
          <div class="form-grid">
            <FormField label="名称"><input v-model="createForm.name" data-testid="personas-name-input" /></FormField>
            <FormField label="模式">
              <select v-model="createForm.mode">
                <option value="observe">observe</option>
                <option value="chat">chat</option>
              </select>
            </FormField>
          </div>
          <FormField label="System Prompt"><textarea v-model="createForm.system_prompt" data-testid="personas-prompt-input" /></FormField>
          <button class="button primary" data-testid="personas-create-submit" :disabled="saving || !canCreate" @click="createPersona">{{ saving ? "处理中..." : "创建" }}</button>
        </div>

        <div class="panel">
          <h2>会话人设绑定</h2>
          <div class="form-grid">
            <FormField label="会话 ID"><input v-model="switchForm.context_key" /></FormField>
            <FormField label="人设">
              <select v-model="switchForm.persona">
                <option value="">默认人设</option>
                <option v-for="name in personas" :key="name" :value="name">{{ name }}</option>
              </select>
            </FormField>
          </div>
          <button class="button primary" :disabled="saving || !switchForm.context_key.trim()" @click="switchPersona">{{ saving ? "处理中..." : "保存绑定" }}</button>
          <DataTable :columns="bindingColumns" :rows="bindingRows" row-key="context_key">
            <template #persona="{ row }">
              <RouterLink class="button small" :to="`/personas/${encodeURIComponent(row.persona)}`">{{ row.persona }}</RouterLink>
            </template>
          </DataTable>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { RouterLink } from "vue-router";
import { api, postJson } from "../api/client";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import FormField from "../ui/FormField.vue";
import MetricCard from "../ui/MetricCard.vue";
import StatusBadge from "../ui/StatusBadge.vue";

const toast = useToastStore();
const personas = ref<string[]>([]);
const items = ref<any[]>([]);
const activePersonas = ref<Record<string, string>>({});
const createForm = reactive({ name: "", system_prompt: "", mode: "observe" });
const switchForm = reactive({ context_key: "", persona: "" });
const loading = ref(false);
const saving = ref(false);
const error = ref("");
const actionResult = ref("");
const query = ref("");
const filter = ref<"all" | "draft" | "learning" | "bound">("all");
const columns = [
  { key: "name", label: "名称" },
  { key: "mode", label: "模式" },
  { key: "published", label: "发布" },
  { key: "draft", label: "草稿" },
  { key: "rag_count", label: "RAG" },
  { key: "sample_messages_total", label: "样本" },
  { key: "learning", label: "学习" },
  { key: "sessions", label: "绑定会话" }
];
const bindingColumns = [
  { key: "context_key", label: "会话" },
  { key: "persona", label: "人设" }
];
const bindingRows = computed(() => Object.entries(activePersonas.value).map(([context_key, persona]) => ({ context_key, persona })));
const draftCount = computed(() => items.value.filter((item) => item.has_unpublished_changes).length);
const learningCount = computed(() => items.value.filter((item) => item.learning?.enabled).length);
const boundSessionCount = computed(() => items.value.reduce((sum, item) => sum + (item.bound_sessions || []).length, 0));
const canCreate = computed(() => Boolean(createForm.name.trim() && createForm.system_prompt.trim()));
const filteredItems = computed(() => {
  const text = query.value.trim().toLowerCase();
  return items.value.filter((item) => {
    if (filter.value === "draft" && !item.has_unpublished_changes) return false;
    if (filter.value === "learning" && !item.learning?.enabled) return false;
    if (filter.value === "bound" && !(item.bound_sessions || []).length) return false;
    if (!text) return true;
    const sessions = (item.bound_sessions || []).map((session: any) => session.context_key).join(" ");
    return `${item.name} ${item.mode || ""} ${sessions}`.toLowerCase().includes(text);
  });
});

function learningLabel(value: any) {
  if (!value?.available) return "-";
  if (!value.configured) return "未配置";
  const targets = (value.qq_ids || []).length + (value.groups || []).length;
  return `${value.enabled === false ? "暂停" : "开启"} / ${targets} 目标 / ${value.total_learned || 0} 条`;
}

function errorText(err: unknown, fallback: string) {
  return err instanceof Error ? err.message : fallback;
}

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const list = await api<{ personas: string[]; items?: any[] }>("/api/personas");
    personas.value = list.personas || [];
    items.value = list.items || personas.value.map((name) => ({ name, bound_sessions: [] }));
    const active = await api<{ active_personas: Record<string, string> }>("/api/persona/active");
    activePersonas.value = active.active_personas || {};
  } catch (err) {
    error.value = errorText(err, "人设列表加载失败");
  } finally {
    loading.value = false;
  }
}
async function runAction(label: string, operation: () => Promise<any>) {
  saving.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await operation();
    actionResult.value = label;
    toast.push(label);
    await load();
    return true;
  } catch (err) {
    error.value = errorText(err, `${label}失败`);
    return false;
  } finally {
    saving.value = false;
  }
}
async function createPersona() {
  if (!canCreate.value) return;
  const ok = await runAction("人设已创建", () => postJson("/api/persona/create", createForm));
  if (ok) {
    createForm.name = "";
    createForm.system_prompt = "";
  }
}
async function switchPersona() {
  if (!switchForm.context_key.trim()) return;
  await runAction(switchForm.persona ? "绑定已保存" : "绑定已清除", () => postJson("/api/persona/switch", switchForm));
}
onMounted(load);
</script>

<style scoped>
.save-result {
  border: 1px solid #386f47;
  background: #142016;
  color: var(--success);
  border-radius: 8px;
  padding: 10px 12px;
}
</style>
